import { ArenaType } from '../lib/types';
import { getUtcDayKey } from './utils';

// Type-only re-export so consumers can pull ArenaType from here; `export type`
// is erased by babel so the backend rollup bundle doesn't treat it as a value.
export type { ArenaType };

/**
 * Pure Arena (ranked async PvP) logic: Elo rating, matchmaking, ticket refill,
 * and season bookkeeping. Kept free of Firebase/warriorUtils imports (like
 * questUtils) so it is trivially unit-testable and shared by client + backend.
 *
 * v1 rating model is *attacker-only*: a match is fought against a frozen,
 * isBot-flagged clone of the opponent, and only the challenger's rating moves.
 * The opponent's stored rating is used purely as the Elo reference, so an
 * offline player can never be farmed down. Two-sided (zero-sum) is a later
 * upgrade that would also apply `nextRating` to the defender with a daily cap.
 */

/** Rating a warrior starts (and is seeded) at. */
export const BASE_RATING = 1000;
/** Ratings never fall below this — also the season soft-reset anchor. */
export const RATING_FLOOR = 800;
/** Elo K-factor (rating swing per match). */
export const K_FACTOR = 32;
/** Full ticket bar; refilled once per UTC day. */
export const MAX_TICKETS = 5;
/** Only warriors within this many levels are matchmaking-eligible. */
export const LEVEL_BAND = 3;
/** Widening rating windows tried in order until a match is found. */
export const RATING_BANDS = [50, 100, 200, 400, Infinity];

/** Minimal shape matchmaking needs; WarriorType structurally satisfies it. */
export type ArenaWarrior = {
  id: string;
  level: number;
  combat?: string;
  arena?: Partial<ArenaType> & { rating: number };
};

/** A warrior's current rating, defaulting unranked warriors to the base. */
export function ratingOf(w: ArenaWarrior): number {
  return w.arena ? w.arena.rating : BASE_RATING;
}

/** Elo expected score for A against B (probability A wins), in (0, 1). */
export function expected(ratingA: number, ratingB: number): number {
  return 1 / (1 + 10 ** ((ratingB - ratingA) / 400));
}

/** New rating for A after a match vs B, floored at RATING_FLOOR. */
export function nextRating(ratingA: number, ratingB: number, won: boolean): number {
  const updated = Math.round(ratingA + K_FACTOR * ((won ? 1 : 0) - expected(ratingA, ratingB)));
  return Math.max(RATING_FLOOR, updated);
}

/** A fresh Arena record for a warrior joining the given season. */
export function initArena(seasonId: string, now: number): ArenaType {
  return {
    rating: BASE_RATING,
    seasonId,
    wins: 0,
    losses: 0,
    tickets: MAX_TICKETS,
    lastRefillAt: now,
  };
}

/**
 * Refill tickets to full on the first request of a new UTC day. Same-day calls
 * are a no-op, so ad/IAP-granted tickets above the cap survive until midnight.
 */
export function refillTickets(arena: ArenaType, now: number): ArenaType {
  if (getUtcDayKey(now) === getUtcDayKey(arena.lastRefillAt)) return arena;
  return { ...arena, tickets: MAX_TICKETS, lastRefillAt: now };
}

/**
 * The fields a match settlement owns. Tickets are deliberately excluded: they
 * are spent up-front by findArenaMatch, and saveWarriorArena merges, so a
 * settlement that echoed them back would overwrite the spend with whatever the
 * pre-match read happened to hold (or mint a full bar via the initArena
 * fallback when the record is missing).
 */
export function settlementFields(arena: ArenaType): Partial<ArenaType> {
  const { rating, seasonId, wins, losses, lastMatchAt } = arena;
  return { rating, seasonId, wins, losses, lastMatchAt };
}

/**
 * Tickets a warrior can spend right now, refill included. The stored count is
 * only rewritten when a match actually starts, so reading `arena.tickets` raw
 * shows yesterday's drained bar — and gating the Find-match button on that
 * locks the player out of the one call that would refill it.
 */
export function availableTickets(arena: ArenaType | undefined | null, now: number): number {
  if (!arena) return MAX_TICKETS;
  return refillTickets(arena, now).tickets;
}

/**
 * Deterministic FNV-1a hash → seed in [0, 1). Mirrors questUtils so client and
 * backend derive the same choice from the same seed string (no Math.random).
 */
function seedFrom(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 0x100000000;
}

/**
 * Pick a ranked opponent for `me` from `pool`, trying ever-wider rating bands.
 * Excludes self, warriors already in combat, and anyone outside the level band.
 * `seedStr` makes the in-band choice deterministic (backend passes an id+time
 * seed for variety); returns null when no live player fits — the caller then
 * synthesizes an island bot so a match is always available.
 */
export function pickOpponent(
  me: ArenaWarrior,
  pool: ArenaWarrior[],
  seedStr = me.id,
): ArenaWarrior | null {
  const eligible = pool.filter(
    (w) => w.id !== me.id && !w.combat && Math.abs(w.level - me.level) <= LEVEL_BAND,
  );
  if (!eligible.length) return null;

  const myRating = ratingOf(me);
  for (const band of RATING_BANDS) {
    const inBand = eligible.filter((w) => Math.abs(ratingOf(w) - myRating) <= band);
    if (inBand.length) {
      const idx = Math.floor(seedFrom(`${seedStr}:${band}`) * inBand.length);
      return inBand[idx];
    }
  }
  return null;
}

/**
 * Settle a finished match into the challenger's Arena record (attacker-only):
 * apply the Elo delta against the opponent's reference rating, bump the season
 * W/L, and stamp the match time. Returns a new record; does not mutate input.
 */
export function settleMatch(
  arena: ArenaType,
  opponentRating: number,
  won: boolean,
  now: number,
): ArenaType {
  return {
    ...arena,
    rating: nextRating(arena.rating, opponentRating, won),
    wins: arena.wins + (won ? 1 : 0),
    losses: arena.losses + (won ? 0 : 1),
    lastMatchAt: now,
  };
}

/** Season rollover: halve the distance above the floor, so top players still start ahead. */
export function softReset(rating: number): number {
  return Math.max(RATING_FLOOR, Math.round(RATING_FLOOR + (rating - RATING_FLOOR) * 0.5));
}

/**
 * ISO-8601 week id (e.g. "2026-w34") used as the season key. A pure function of
 * the timestamp — the weekly EventBridge rollover and clients agree on "which
 * season" without any stored pointer, the same way daily quests derive from a
 * UTC day key.
 */
export function getSeasonId(now: number): string {
  const d = new Date(now);
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // Shift to the Thursday of the current ISO week (Mon=0 … Sun=6).
  const day = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDay = (firstThursday.getUTCDay() + 6) % 7;
  const week =
    1 + Math.round(((date.getTime() - firstThursday.getTime()) / 86400000 - 3 + firstDay) / 7);
  return `${date.getUTCFullYear()}-w${String(week).padStart(2, '0')}`;
}
